import { extend, merge, trimEnd, noop, get, cloneDeep } from 'lodash';
import { URI } from './urijs';
const XSRF_HEADER_NAME = 'x-xsrf-token';
const appendPetriOvr = (path, petriOvr) => {
    if (!petriOvr) {
        return path;
    }
    const uri = new URI(path);
    uri.setQuery('petri_ovr', petriOvr);
    return URI.decode(uri.valueOf());
};
export const createRequest = ({ baseUrl = '', httpClient, getInstance = () => null, locale = 'en', cookie = null, performanceTracker = { trackStart: noop, trackEnd: noop }, siteRevision = null, trackError = () => { }, logResponse = () => { }, petriOvr = '', csrfToken = null, isSSR = false, disableRetryOnError = false, }) => {
    const request = (path, config = {}) => {
        const url = appendPetriOvr(`${trimEnd(baseUrl, '/')}${path}`, petriOvr);
        const shouldParseHeaders = config.parseHeaders;
        delete config.parseHeaders;
        const instance = getInstance();
        config = decorateConfig(config, instance, cookie, locale, siteRevision, csrfToken);
        const marker = performanceTracker.trackStart(`${config.method || 'GET'} ${path}`);
        const shouldRetryOnGatewayTimeout = (response) => {
            if (disableRetryOnError) {
                return false;
            }
            return config.noRetriesOnErrors ? !config.noRetriesOnErrors.includes(response.status) : true;
        };
        const start = () => {
            return httpClient.request(Object.assign({ url }, config));
        };
        const parse = (response) => {
            return Promise.resolve(response)
                .then(handleSuccess)
                .then(shouldParseHeaders ? parseHeadersAndBody : parseBody)
                .then((r) => {
                performanceTracker.trackEnd(marker);
                return r;
            })
                .then((r) => {
                const headersList = [];
                try {
                    if (r && r.headers && r.headers.entries) {
                        for (const h of r.headers.entries()) {
                            headersList.push(h);
                        }
                    }
                    else if (r && r.headers) {
                        headersList.push(JSON.stringify(r.headers));
                    }
                }
                catch (error) {
                    trackError(error);
                }
                logResponse([path, r, headersList]);
                return r;
            })
                .catch((error) => handleError(error, path, trackError));
        };
        const sleep = (timeout) => new Promise((resolve) => setTimeout(resolve, timeout));
        const retry = (fn, retries = 3, timeout = 300) => new Promise((resolve, reject) => fn().then((response) => {
            if (!isSSR &&
                response.status >= 500 &&
                retries > 1 &&
                shouldRetryOnGatewayTimeout(response)) {
                return sleep(timeout)
                    .then(() => retry(fn, retries - 1, timeout * 2))
                    .then(resolve, reject);
            }
            resolve(response);
            return response;
        }, (error) => {
            if (!isSSR && retries > 1 && shouldRetryOnGatewayTimeout(error.response)) {
                return sleep(timeout)
                    .then(() => retry(fn, retries - 1, timeout * 2))
                    .then(resolve, reject);
            }
            reject(error.response);
            return error;
        }));
        return retry(start).then(parse);
    };
    request.post = (path, data, config) => {
        data = data || {};
        return request(path, merge({}, config, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            data,
        }));
    };
    request.put = (path, data, config) => {
        data = data || {};
        return request(path, merge({}, config, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            data,
        }));
    };
    request.patch = (path, data, config) => {
        data = data || {};
        return request(path, merge({}, config, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
            data,
        }));
    };
    request.delete = (path, config) => {
        return request(path, extend({}, config, {
            method: 'DELETE',
        }));
    };
    request.uploadFile = (file, credentials) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('media_type', 'picture');
        formData.append('upload_token', credentials.uploadToken);
        formData.append('parent_folder_id', credentials.folderId);
        const config = {
            method: 'POST',
            body: formData,
        };
        return fetch(credentials.uploadUrl, config)
            .then(handleUploadSuccess)
            .then((response) => response.json().catch(() => Promise.resolve()));
    };
    request.httpClient = httpClient;
    return request;
};
const handleUploadSuccess = (response) => {
    const status = response.status;
    if (status < 400) {
        return response;
    }
    return Promise.reject(response);
};
const handleSuccess = (response) => {
    const status = response.status;
    if (status < 400) {
        return response;
    }
    return Promise.reject(response);
};
// ---------- HttpClient API ----------
function decorateConfig(config, instance, cookie, locale, siteRevision, csrfToken) {
    const httpClientConfig = cloneDeep(config);
    httpClientConfig.method = httpClientConfig.method || 'GET';
    httpClientConfig.headers = httpClientConfig.headers || {};
    httpClientConfig.headers.locale = locale;
    // csrf token should be handled internally, but workaround for worker environment is need for now
    if (csrfToken) {
        httpClientConfig.headers[XSRF_HEADER_NAME] = csrfToken;
    }
    // Instance is used in legacy APIs, should be removed in the future as Authorization header is enough
    if (instance) {
        httpClientConfig.headers.instance = instance;
    }
    if (cookie) {
        httpClientConfig.headers.cookie = cookie;
    }
    if (siteRevision) {
        httpClientConfig.headers['x-wix-site-revision'] = siteRevision;
    }
    return httpClientConfig;
}
const parseBody = (response) => {
    return response.data;
};
const parseHeadersAndBody = (response) => {
    return {
        body: response.data,
        headers: response.headers,
    };
};
const handleError = (error, path, trackError) => {
    const { response } = error;
    trackError(`request error: path=${path}, status=${get(response, 'status')}, error=${JSON.stringify(response)}`);
    return Promise.reject(response);
};
//# sourceMappingURL=create-request.js.map